/* ====================================================================================
    REMOTE REQUEST FRAMEWORK
   =========================
    Remote request framework is designed to eliminate
    the need for extra javascript for various engagement
    and drive adoption of server-side rendering.
    Primary candidates for this framework are the microservices
    integrated in the header, cart and cartlet displayed when a
    product is added to the cart.
    This can be used by setting data attributes on the elements
    without disrupting the default HTML attributes so the functionality
    continues to work even if the browser javascript is disabled.
    Available data-attributes:
    1. data-remote-url:
       Remote URL to connect to for the request.
    2. data-remote-method:
       This parameter needs to be set for "POST" request.
       Default value is "GET". Default value for forms is "POST".
    3. data-remote-container:
       This is a comma separated CSS selectors to identify the container(s)
       for the request to display the content. Ideally, this will be a
       comma separated list of unique IDs.
    4. data-remote-container-toggle-classes:
       This is a CSS class to be added to make the remote container visible.
    5. data-remote-params:
       This is a stringified JSON of params to be sent as a POST request
       body. This should be capped at 3. If there are more than 3 params,
       please consider custom script for handling this. Additionally, do not
       allow rich text or complex values for the variables. These values
       can be simple booleans or some IDs. If anything more, consider
       creating a form instead.
    6. data-remote-headers:
       This is a stringified JSON of params to be sent with any GET or POST
       request. Default 'utcOffset' request header property is always sent
       as part of the request with the value of user timezone's UTC offset value.
    7. data-remote-update-content:
       This is a simple boolean field. It should be set to true and only used
       when the method of request is other than "GET". Set it to true when content
       is supposed to be updated in case of other request types. It should be set to false
       and only necessary when the method of request is "GET" to ensure the container
       is not updated with the response content.
    8. data-remote-frame:
       This is a simple boolean field. It should be set to true for when you
       want to fetch remote content on initialization and present it in a container.
       It treats the container like an iFrame to fetch the remote content and place
       it in the data-remote-container.
    9. data-remote-trigger-once:
       This is a simple boolean field. It should be set to true if the request
       should only be triggered once after initialization.
    10.data-remote-triggered:
       This is simple boolean value added by the framework when the request is made once.
    11.data-remote-success-url:
       This is url to be redirected to when the RemoteRequest is successful.
    12.data-remote-failure-url:
       This is url to be redirected to when the RemoteRequest is failed.
    13.data-parse-scripts
       This is a boolean value set to true when you want to parse out any <script> tags from the request response and append to the body.
    14.data-accept-header
       Pass in custom accepts header in request
    15.data-response-type
       pass in expected response type from server
       16.data-remote-callback-function
       function to fire when remote call is completed ( added for CA )

    Examples
   =========================
    Example Link:
    Notifications icon when clicked displays the notification panel.
    <a class="header-icon-link komodo-remote-request"
        href="/path/to/the/notifications/page"
        data-remote-url="/path/to/the/notifications/panel/content"
        data-remote-container="#user-notifications"
        data-remote-container-toggle-classes="active">
      <i class="ico-bell-icon red-icon"></i>
    </a>
    Scenarios:
    Javascript Disabled
    When a user interacts with the above link, since javascript is disabled,
    user will be navigated to the notifications page.
    Javascript Enabled
    When a user interacts with the above link, script will check
    if the user is on a Mobile screen size.
    If mobile size, user will be navigated to the notifications page.
    If NOT mobile size, data-remote-url will be used to fetch the content
    for the panel. data-remote-container selector will be used to display
    the content. Once the content is added to the container,
    data-remote-container-toggle-class will be added to make the container visible.
    Example Form:
    Add to Cart form when submitted displays the cartlet for the item added.
    <form method="POST" class="komodo-remote-request" action="/path/to/the/action"
          data-remote-url="/path/to/the/remote/action"
          data-remote-container="#cart-sidebar"
          data-remote-container-toggle-classes="active">
      some form fields...
    </form>

    Scenarios:
    Javascript Disabled
    When the user submits the form, the default action path will be used which
    SHOULD add item to their cart and redirect them back to their page.
    Javascript Enabled
    When the user submits the form, the data-remote-url will be used to submit the
    form data and will expect to return the cart template to be returned as a response.
    The response will be added to the data-remote-container. Once done, remote
    container will be made visible by adding the data-remote-container-toggle-classes.
====================================================================================== */
document.addEventListener('DOMContentLoaded', function () {
  'use strict'
  // Class constructor for Komodo Remote Request component.
  //
  // @constructor
  // @param {HTMLElement} The element that will be upgraded to be Komodo Remote Request component.
  var KomodoRemoteRequest = function KomodoRemoteRequest(element) {
    this.element_ = element;
    this.responseContainers = document.querySelectorAll(this.element_.getAttribute("data-remote-container").split(","));

    this.validateRequest();

    if (this.element_.getAttribute("data-remote-container-toggle-classes"))  {
      this.responseActiveClasses = this.element_.getAttribute("data-remote-container-toggle-classes").split(",");
    }

    this.requestMethod = this.element_.getAttribute("data-remote-method");
    if (((!this.requestMethod || this.requestMethod === "GET") && !(this.element_.getAttribute("data-remote-update-content") === "false"))
        || this.element_.getAttribute("data-remote-update-content") === "true"){
        this.updateContainersWithResponse = true;
    }

    if(this.element_.getAttribute("data-remote-frame")){
      this.makeRequest();
    }
    if (this.element_.tagName === "FORM") {
      this.element_.addEventListener("submit", function (e) {
        e.preventDefault();
        this.submitForm();
      }.bind(this));
    } else {
      this.element_.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        this.makeRequest();
      }.bind(this));
    }
  };
  window['KomodoRemoteRequest'] = KomodoRemoteRequest

  // Store strings for class names defined by this component that are used in
  // Javascript. This allows us to simply change it in one place should we decide
  // to modify at a later date.
  //
  // @enum {string}
  // @private
  KomodoRemoteRequest.prototype.constants_ = {
    MOBILE_WIDTH: 320,
    LOADING: "<div class='c-loader'></div>",
    SCRIPT_REGEX: /<script\b[^>]*>([\s\S]*?)<\/script>/gi
  };

  // RemoteRequest check viewport.
  // Return true if in Desktop or Tablet Mode.
  // Return false if in Mobile mode.
  //
  // @public
  KomodoRemoteRequest.prototype.checkViewport = function () {
    return window.innerWidth > this.constants_.MOBILE_WIDTH;
  };
  KomodoRemoteRequest.prototype["checkViewport"] = KomodoRemoteRequest.prototype.checkViewport;

  // RemoteRequest make request.
  // Makes the request, updates the content or gracefully fails.
  //
  // @public
  KomodoRemoteRequest.prototype.makeRequest = function () {
    if(this.element_.getAttribute("data-remote-trigger-once") && this.element_.getAttribute("data-remote-triggered")){
      console.error({
        element: this.element_,
        error: "Remote Request: Request already made.",
        message: "Element does not allow for repeated requests. To allow, remove data-remote-trigger-once attribute."
      })
      return;
    }
    var requestObject = {
      method: this.requestMethod,
      headers: {}
    };
    if(this.buildURL(this.element_.getAttribute("data-remote-url"))){
      requestObject.url = this.buildURL(this.element_.getAttribute("data-remote-url"));
    } else {
      return;
    }
    if(this.element_.getAttribute("data-accept-header")){
      requestObject.headers.accept = this.element_.getAttribute("data-accept-header");
    }
    if(this.element_.getAttribute("data-response-type")){
      requestObject.responseType = this.element_.getAttribute("data-response-type");
    }
    requestObject.headers.utcOffset = new Date().getTimezoneOffset();
    if(this.element_.getAttribute("data-remote-headers")){
      Object.assign(requestObject.headers, JSON.parse(this.element_.getAttribute("data-remote-headers")));
    }
    if (this.element_.getAttribute("data-remote-params")) {
      requestObject.body = this.element_.getAttribute("data-remote-params");
    }
    this.promisedRequest(requestObject, this.successfulRequest.bind(this), this.erroredRequest.bind(this))
    this.element_.dispatchEvent(componentHandler.createEvent('start.komodo.remoteRequest', null, false, true))
  };
  KomodoRemoteRequest.prototype["makeRequest"] = KomodoRemoteRequest.prototype.makeRequest;

  // RemoteRequest promised request
  // Makes the request and handles the response.
  //
  // @param {JSON} Object is a JSON object with URL, Method and Body Attributes.
  // @param {Function} Function to be executed when the request is successful.
  // @param {Function} Function to be executed when the request fails.
  // @public
  KomodoRemoteRequest.prototype.promisedRequest = function (obj, resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.open(obj.method || "GET", obj.url, true);
    xhr.withCredentials = true;
    if (obj.headers) {
      for (var i = 0; i < Object.keys(obj.headers).length; i++) {
        xhr.setRequestHeader(Object.keys(obj.headers)[i], obj.headers[Object.keys(obj.headers)[i]])
      }
    }
    if(obj.responseType){
      xhr.responseType = obj.responseType;
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState === xhr.DONE) {
        if ((xhr.status < 200 || xhr.status > 300) && xhr.status !== 0) {
          reject({code: xhr.status, response: xhr.statusText})
        } else {
          resolve({code: 200, response: xhr.response})
        }
      }
    }
    xhr.onerror = function(){
      if(xhr.status !== 0){
        reject({code: xhr.status, response: xhr.statusText})
      }
    }
    xhr.send(obj.body)

    // Add loader icon on response containers
    if (this.updateContainersWithResponse) {
      this.showLoading();
    }
  };
  KomodoRemoteRequest.prototype["promisedRequest"] = KomodoRemoteRequest.prototype.promisedRequest;

  // RemoteRequest form submit.
  // This function will submit the form via AJAX and handle the response similar to promisedRequest.
  //
  // @public
  KomodoRemoteRequest.prototype.submitForm = function () {
    this.promisedRequest({
      method: this.requestMethod,
      url: this.element_.getAttribute("data-remote-url"),
      body: new FormData(this.element_)
    }, this.updateContent.bind(this), this.erroredRequest.bind(this))
  }
  KomodoRemoteRequest.prototype["submitForm"] = KomodoRemoteRequest.prototype.submitForm;

  // RemoteRequest successful request
  // This function is executed on the successful response from the service request.
  // Based on the attributes available, it decides which function to call.
  //
  // @public
  KomodoRemoteRequest.prototype.successfulRequest = function (response) {
    if(this.element_.getAttribute("data-remote-success-url")){
      this.element_.dispatchEvent(componentHandler.createEvent('successful.komodo.remoteRequest', response.response, false, true))
      this.redirectRequest(this.element_.getAttribute("data-remote-success-url"));
    } else {
      this.updateContent(response);
      if(this.element_.getAttribute("data-parse-scripts")){
        this.appendScripts(response);
      }
      if(this.element_.getAttribute("data-remote-callback-function")){
        window[this.element_.getAttribute("data-remote-callback-function")](response);
      }
    }
  }

  // RemoteRequest redirect request
  // Redirects to url param link.
  //
  // @private
  KomodoRemoteRequest.prototype.redirectRequest = function (url) {
    window.location.href = this.buildURL(url);
  }

  // RemoteRequest update content
  // Checks to see if the content needs to be updated. Set the content to the response
  // body and make the container visible.
  //
  // @param {Response} Request response from the server depending on the request type.
  // @public
  KomodoRemoteRequest.prototype.updateContent = function (response) {
    this.element_.setAttribute("data-remote-triggered", true);
    if (this.updateContainersWithResponse) {
      for (var i = 0; i < this.responseContainers.length; i++) {
        this.responseContainers[i].innerHTML = response.response;
      }
    }
    if (this.responseActiveClasses) {
      for (var i = 0; i < this.responseContainers.length; i++) {
        this.responseContainers[i].classList.toggle(this.responseActiveClasses);
      }
    }
    this.element_.dispatchEvent(componentHandler.createEvent('successful.komodo.remoteRequest', response.response, false, true))
  }
  KomodoRemoteRequest.prototype["updateContent"] = KomodoRemoteRequest.prototype.updateContent;

  // RemoteRequest parse and append javascript to the DOM
  // Checks to see if any <script> content from the request needs to execute
  //
  // @param {Response} Request response from the server depending on the request type.
  // @public
  KomodoRemoteRequest.prototype.appendScripts = function (response) {
    let matches = response.response.match(this.constants_.SCRIPT_REGEX);
    let parentElement = document.querySelector('body');
    let scriptElement;
    let scriptContent;

    if (matches) {
      for (let i = 0; i < matches.length; i++) {
        scriptContent = matches[i].replace(this.constants_.SCRIPT_REGEX, '$1');
      }
    }
    scriptElement = document.createElement("script");
    scriptElement.textContent = scriptContent;
    parentElement.appendChild(scriptElement)
  }
  KomodoRemoteRequest.prototype["appendScripts"] = KomodoRemoteRequest.prototype.appendScripts;

  // RemoteRequest Errored Request
  // This function is executed when the request has errored.
  // Redirects to failure URL if available, or console logs the response object as an error.
  //
  // @private
  KomodoRemoteRequest.prototype.erroredRequest = function (response) {
    this.element_.dispatchEvent(componentHandler.createEvent('failed.komodo.remoteRequest', response, false, true));
    if(this.element_.getAttribute("data-remote-failure-url")){
      this.redirectRequest(this.element_.getAttribute("data-remote-failure-url"));
    }
    console.error(response);
  }

  // RemoteRequest Loading Icon
  // This function is executed to show loading icon when request is initiated
  //
  // @private
  KomodoRemoteRequest.prototype.showLoading = function () {
    for (var i = 0; i < this.responseContainers.length; i++) {
      this.responseContainers[i].innerHTML = this.constants_.LOADING;
    }
  }

  // RemoteRequest Build URL
  // This function ensures the URLs are updated to be a valid url.
  //
  // @pirvate
  KomodoRemoteRequest.prototype.buildURL = function (url) {
    return url; //just for dev
    var urlRegex = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g;
    if (urlRegex.test(url) || url.startsWith("//www")){
      return url;
    } else if (urlRegex.test(window.location.origin+url)){
       return window.location.origin + url;
    } else {
      console.error({
        element: this.element_,
        error: "Remote Request: Failed Initialization",
        message: "Failed to initialize remote-request object. Remote URL not valid."
      })
      return false;
    }
  }

  // RemoteRequest Validate Config
  // This function is executed to validate the request configuration.
  //
  // @private
  KomodoRemoteRequest.prototype.validateRequest = function () {
    var errors = [];
    if(this.element_.getAttribute("data-remote-method") &&
      ["GET", "POST", "PUT", "PATCH"].indexOf(this.element_.getAttribute("data-remote-method")) === -1){
      errors.push({
        error: "Remote Request: Failed Initialization",
        message: "Failed to initialize the remote-request object. Request type is not valid."
      })
    }
    if(this.responseContainers.length === 0){
      errors.push({
        error: "Remote Request: Failed Initialization",
        message: "Failed to initialize the remote-request object. Response container selector is not valid."
      })
    }
    if(!this.element_.getAttribute("data-remote-url")){
      errors.push({
        error: "Remote Request: Failed Initialization",
        message: "Failed to initialize the remote-request object. Remote URL not set."
      })
    }
    if (errors.length > 0) {
      console.error({
        element: this.element_,
        errors: errors
      })
    }
    return;
  }

  // The component registers itself. It can assume componentHandler is available
  // in the global space.
  componentHandler.register({
    constructor: KomodoRemoteRequest,
    classAsString: "KomodoRemoteRequest",
    cssClass: "komodo-remote-request",
    widget: true
  })
})